import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Wallet,
  Receipt,
  Clock,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  TrendingUp
} from "lucide-react";

const PortalDashboard = () => {
  const { API, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/portal/dashboard`, { headers });
        setStats(res.data);
      } catch (err) {
        console.error("Error fetching portal stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const cards = [
    {
      title: "Mis Declaraciones",
      value: stats?.declaraciones || 0,
      icon: FileText,
      color: "bg-blue-50 text-blue-700",
      iconColor: "text-blue-600",
      path: "/portal/declaracion"
    },
    {
      title: "Monto Pendiente",
      value: `Bs. ${(stats?.monto_pendiente || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}`,
      icon: AlertTriangle,
      color: "bg-amber-50 text-amber-700",
      iconColor: "text-amber-600",
      path: "/portal/pendientes"
    },
    {
      title: "Comprobantes Pagados",
      value: stats?.comprobantes_pagados || 0,
      icon: CheckCircle,
      color: "bg-green-50 text-green-700",
      iconColor: "text-green-600",
      path: "/portal/comprobantes"
    },
    {
      title: "Declaraciones Pendientes",
      value: stats?.declaraciones_portal_pendientes || 0,
      icon: Clock,
      color: "bg-purple-50 text-purple-700",
      iconColor: "text-purple-600",
      path: "/portal/declaracion"
    }
  ];

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-1" data-testid="portal-welcome">
          Portal del Contribuyente
        </h1>
        <p className="text-green-100">
          Bienvenido, {stats?.contribuyente_nombre || "Contribuyente"}
        </p>
        <div className="mt-2">
          <Badge className="bg-white/20 text-white border-0">
            RIF: {stats?.contribuyente_rif || "N/A"}
          </Badge>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.title}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(card.path)}
              data-testid={`portal-card-${card.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{card.title}</p>
                    <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.color}`}>
                    <Icon className={`w-5 h-5 ${card.iconColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Nueva Declaracion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Cree una nueva declaracion tributaria y cargue sus documentos de soporte.
            </p>
            <Button
              onClick={() => navigate("/portal/declaracion")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-nueva-declaracion"
            >
              Ir a Declaraciones <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" />
              Reportar Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Genere un reporte de pago para sus declaraciones pendientes.
            </p>
            <Button
              onClick={() => navigate("/portal/reporte-pago")}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-reporte-pago"
            >
              Reportar Pago <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              Mis Comprobantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Consulte sus comprobantes de pago y recibos generados.
            </p>
            <Button
              onClick={() => navigate("/portal/comprobantes")}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="btn-mis-comprobantes"
            >
              Ver Comprobantes <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortalDashboard;
